import { Component, inject, linkedSignal, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ResponsiveMenuComponent } from '@softapps/core';
import { MetaFormCellComponent, PageHeaderComponent, QueryPageComponent } from '@softapps/core/pages';
import { FieldOkObjekt, ObjektFieldOkComponent } from '@softapps/wws/core';
import {
  backNavigation,
  menuItems,
  queryParam,
  subtitle,
  title } from '@softline/application';
import { DateService } from '@softline/core';
import { DynamicModule } from '@softline/dynamic';
import { translator} from '@softline/ui-core';
import 'moment/locale/de';
import { timeReportQueryMeta } from '../../models/meta/time-report-query.meta';
import { TimeReportQuery } from '../../models/time-report.query';

import { DateRangeFromInputComponent } from '@softline/ui-core/date';
import { DateRangeToInputComponent } from '@softline/ui-core/date';
import { FieldValidationDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-time-report-query',
  templateUrl: './query.component.html',
  styleUrls: ['./query.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    DynamicModule,
    QueryPageComponent,
    PageHeaderComponent,
    ResponsiveMenuComponent,
    ObjektFieldOkComponent,
    MetaFormCellComponent,
    DateRangeFromInputComponent,
    DateRangeToInputComponent,
    FieldValidationDirective,
    TranslatePipe] })
export class QueryComponent {
  private readonly router = inject(Router);
  private readonly dateService = inject(DateService);
  private readonly dateQueryParam = queryParam('date');

  private readonly t = translator();

  public readonly date = linkedSignal(
    () => this.dateQueryParam() ?? this.dateService.now()
  );

  public readonly timeReportQueryMeta = timeReportQueryMeta;

  public readonly form = new FormGroup({
    from: new FormControl<string | null>(null),
    to: new FormControl<string | null>(null),
    objekt: new FormControl<FieldOkObjekt | null>(null) });

  public readonly shortcuts = signal([
    {
      title: '#TIME_REPORT.COMPONENTS.QUERY.QUICK_LINKS.CURRENT_WEEK',
      icon: 'fa-regular fa-calendar-day',
      query: { from: 'mo', to: 'fr' } },
    {
      title: '#TIME_REPORT.COMPONENTS.QUERY.QUICK_LINKS.LAST_WEEK',
      icon: 'fa-regular fa-calendar-week',
      query: { from: 'mo-1', to: 'fr-1' } },
    {
      title: '#TIME_REPORT.COMPONENTS.QUERY.QUICK_LINKS.CURRENT_MONTH',
      icon: 'fa-regular fa-calendar-lines',
      query: { from: 'ma', to: 'me' } }]);

  constructor() {
    backNavigation('/arbeitsbericht');
    title(this.t('#TIME_REPORT.TITLE'));
    subtitle('Abfrage');
    menuItems(() => [
      {
        type: 'route',
        icon: 'fa-regular fa-plus',
        name: 'create-1',
        title: 'Neuer Eintrag',
        priority: 1,
        outlet: 'nav',
        routerLink: ['/arbeitsbericht', 'create'],
        queryParams: { date: this.date() } },
      {
        name: 'arbeitsberichtOverview',
        type: 'route',
        priority: 2,
        outlet: 'nav',
        icon: 'fa-regular fa-calendar-check',
        title: '#TIME_REPORT.MENU.LIST',
        routerLink: ['/arbeitsbericht'] },
      {
        name: 'arbeitsberichtQuery',
        type: 'route',
        outlet: 'nav',
        priority: 3,
        icon: 'fa-regular fa-magnifying-glass',
        title: '#TIME_REPORT.MENU.QUERY',
        routerLink: ['/arbeitsbericht', 'query'] }]);
  }

  readonly navigateToQueryResultPage = async (query: TimeReportQuery): Promise<void> => {
    await this.router.navigate(['/arbeitsbericht', 'result'], {
      queryParams: {
        from: query.from,
        to: query.to,
        objektid: query.objekt?.id } });
  };
}
